<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import { XMarkIcon, CheckIcon } from '@heroicons/vue/24/outline';
import { useI18n } from "vue-i18n";
import { useNotificationsStore } from '@/stores/notifications';
import { DialogService } from '@/utils/dialogService.js'
import { useUserStore } from '@/stores/user'
import notificationsApi from '@/services/api/notifications/notifications';
import ButtonOutline from '@/Components/UI/ButtonOutline.vue';
import HeroIcon from '@/Components/UI/HeroIcon.vue'
import Modal from '@/Components/UI/Modal.vue'
import NotificationSidebarModal from '@/Components/Notifications/NotificationSidebarModal.vue'

const { t } = useI18n();
const emit = defineEmits(['close']);
const notificationsStore = useNotificationsStore();
const userStore = useUserStore();

const props = defineProps({
  show: Boolean
});
const showPopup = ref(0)
const notifications = ref([])
const selectedNotification = ref(null)
const loading = ref(false);
const page = ref(1)
const lastPage = ref(1)
const hasLoadedOnce = ref(false);

// Antes el `onMounted` de este componente disparaba SIEMPRE la peticion de
// notificaciones aunque el sidebar estuviera cerrado (el componente se monta
// una vez con el layout y despues usa `v-show` para ocultarse). Eso generaba
// dos requests HTTP innecesarios en cada carga inicial. Ahora cargamos la
// lista la primera vez que el usuario abre el sidebar, y el contador de no
// leidas lo actualiza el Navbar al arrancar (fire-and-forget).
const loadNotificationsList = async () => {
  if (hasLoadedOnce.value || loading.value) return;
  try {
    loading.value = true;
    const responseNotifications = await notificationsApi.getNotifications(1);
    const data = responseNotifications.data || [];
    notifications.value = data;
    lastPage.value = responseNotifications.pagination?.last_page || 1;
    notificationsStore.setNotifications(data);
    hasLoadedOnce.value = true;
  } catch (error) {
    console.error('Error fetching initial notifications:', error);
  } finally {
    loading.value = false;
  }
};

watch(() => props.show, (newValue) => {
  if (newValue) {
    loadNotificationsList();
  }
});


const closeSidebar = () => {
  selectedNotification.value = null;
  notifications.value = [];
  page.value = 1;
  emit('close');
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString();
};

const markAllAsRead = async () => {

  DialogService.confirm({
    title: t('app.notifications.dialog.title'),
    description: t('app.notifications.dialog.description'),
    confirmText: t('app.dialog.actions.continue'),
    denyText: t('app.dialog.actions.deny')
  }).then(async (confirmed) => { // Aquí se utiliza async para permitir el uso de await dentro de la función
    if (confirmed) {
      try {
        await notificationsApi.markAllAsRead(); // llamada real al backend

        const currentUserId = userStore.getProfile.id || '';

        // Marca localmente como leídas
        notifications.value = notifications.value.map(notification => {
          if (!notification.readed_by || !notification.readed_by.includes(`,${currentUserId},`)) {
            notification.readed_by = notification.readed_by
              ? `${notification.readed_by},${currentUserId}`
              : `,${currentUserId}`;
          }
          // Asegúrate de marcar el campo 'readed' como 1
          notification.readed = 1;

          return notification;
        });

        notificationsStore.setNotifications(notifications.value); // refresca el store
        notificationsStore.unreadCount = 0; // Actualiza el contador de no leídas
      } catch (error) {
        console.error('Error marking all as read:', error);
      }
    } else {
      // Maneja el caso en que el usuario cancela la acción
    }
  });


};


const isRead = (notification) => {
  // Verifica si el campo 'readed' está presente y es 1
  return notification.readed == 1;
};


const loadMore = async () => {
  if (page.value < lastPage.value && !loading.value) {
    loading.value = true;
    page.value++;
    try {
      const response = await notificationsApi.getNotifications(page.value);
      const newNotifications = response.data || [];
      notifications.value = [...notifications.value, ...newNotifications];

      // Asegura que la reactividad se actualice
      lastPage.value = response.pagination?.last_page || lastPage.value;

      console.log("Updated Last Page:", lastPage.value);

    } catch (error) {
      console.error("Error al cargar más notificaciones:", error);
    } finally {
      loading.value = false;
    }
  }
};


const selectNotification = async (notification) => {
  selectedNotification.value = notification;
  showPopup.value = 1; // Abrir el pop-up

  if (!isRead(notification)) {
    try {
      await notificationsApi.markAsRead(notification.id);
      const index = notifications.value.findIndex(n => n.id === notification.id);
      if (index !== -1) {
        notifications.value[index].readed = 1;
        notificationsStore.unreadCount--;
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }
};


const fetchNotifications = async () => {
  loading.value = true;
  try {
    const response = await notificationsApi.getNotifications(page.value);
    const notificationsArray = Object.values(response.data);
    notifications.value = notificationsArray;
    lastPage.value = response.pagination.last_page;
  } catch (error) {
    console.error("Error fetching notifications:", error);
  } finally {
    loading.value = false;
  }
};

const closeNotificationModal = () => {
  showPopup.value = 0;
  selectedNotification.value = null;
};


watch(() => props.show, (newValue) => {
  if (newValue) {
    fetchNotifications();
  }
});


</script>


<template>
  <transition name="sidebar-transition">
    <div v-show="show" class="fixed inset-0 z-10">
      <div class="absolute right-0 w-[90%] sm:w-1/2 md:w-1/3 bg-white h-full shadow-lg flex flex-col">
        <!-- Header -->
        <div class="p-4 flex flex-row justify-between items-center border-b bg-blue-100">
          <p class="text-lg font-bold flex items-center space-x-2 text-blue-700">
            <HeroIcon iconName="bell" />
            <span>{{ t('app.notifications.title') }}</span>
          </p>

          <button @click="closeSidebar" class="text-blue-500 hover:text-blue-700">
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Notifications List -->
        <div class="flex-1 p-4 overflow-y-auto">
          <div v-if="loading" class="text-center text-gray-500">
            {{ t('app.notifications.loading') }}
          </div>

          <div v-else-if="notifications.length == 0"
            class="flex flex-col items-center justify-center h-full text-center text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
              stroke="currentColor" class="w-32 h-32 text-gray-200">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0M10.5 8.25h3l-3 4.5h3" />
            </svg>
            <p class="text-3xl tracking-tight text-gray-400 sm:text-xl">{{ t('app.notifications.empty') }}</p>
          </div>

          <div v-else class="divide-y">
            <div v-for="notification in notifications" :key="notification.id" @click="selectNotification(notification)"
              class="p-3 cursor-pointer hover:bg-gray-100 transition-colors" :class="{
                'bg-blue-50': selectedNotification && selectedNotification.id === notification.id,
                'font-semibold': !isRead(notification)
              }">
              <div class="flex items-center justify-between">
                <span class="text-sm" :class="isRead(notification) ? 'text-gray-600' : 'text-black'">
                  {{ formatDateTime(notification.date) }}
                </span>
                <CheckIcon v-if="isRead(notification)" class="h-4 w-4 text-green-500" />
                <span v-else class="h-2 w-2 bg-black rounded-full"></span>
              </div>
              <h4 class="text-sm md:text-base truncate" :class="isRead(notification) ? 'text-gray-800' : 'text-black'">
                {{ notification.subject }}
              </h4>
              <p class="text-xs text-gray-500 truncate">
                {{ (notification.preview && notification.preview.substring(0, 60)) || '' }}{{ notification.preview &&
                  notification.preview.length > 60 ? '...' : '' }}
              </p>
            </div>

            <div v-if="page < lastPage" class="p-3 text-center">
              <button @click="loadMore" class="text-sm text-blue-600 hover:text-blue-800" :disabled="loading">
                {{ loading ? t('app.notifications.loadingMore') : t('app.notifications.loadMore') }}
              </button>
            </div>

          </div>
        </div>

        <!-- Mark All as Read Button -->
        <div class="p-4 border-t mt-auto bg-gray-100">
          <ButtonOutline icon="bellSlash" class="w-full text-xs bg-white" :hideLabelOnMobile="false"
            @click="markAllAsRead">
            {{ t('app.notification.allread') }}
          </ButtonOutline>
        </div>

      </div>
    </div>
  </transition>

  <Modal :title="selectedNotification?.subject" :show="showPopup" @onClosed="closeNotificationModal" width="max-w-2xl">
		<NotificationSidebarModal :notification="selectedNotification" />
	</Modal>
    
</template>


<style scoped>
.sidebar-transition-enter-active,
.sidebar-transition-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.sidebar-transition-enter-from,
.sidebar-transition-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.sidebar-transition-enter-to,
.sidebar-transition-leave-from {
  transform: translateX(0);
  opacity: 1;
}

/* Add background gray to popup */
.bg-gray-500 {
  pointer-events: auto;
  /* Allow background interaction */
}

.bg-white {
  pointer-events: auto;
  /* Allow interaction with the pop-up */
}

@media (min-width: 768px) {
  .transform {
    transform: translateX(-25%);
  }
}
</style>